import { Fragment, useEffect, useRef, useState } from "react";
import { gql } from "@apollo/client";
import classNames from "classnames";
import Markdown from "react-markdown";
import { markdownUrlTransform } from "utils/markdownUrlTransform";
import { TextAndMediaCarousel } from "../../../../__generated__/graphql";
import ContentfulMedia from "components-shared/ContentfulMedia";
import { getRedesignThemeColors } from "../theme";
import { ContentfulComponentType } from "../types";
import { isDefined } from "utils/typescript";
import styles from "./TextAndMediaCarousel.module.css";

function useSlideShow({ items }: { items: number }) {
  const [active, setActive] = useState(0);
  const [className, setClassName] = useState("");
  const [isPlaying, setIsPlaying] = useState(true);
  const interval = useRef<NodeJS.Timeout | null>(null);

  const stop = () => {
    setIsPlaying(false);
  };

  useEffect(() => {
    if (!isPlaying && interval.current) {
      setClassName("");
      clearInterval(interval.current);
    } else {
      interval.current = setInterval(() => {
        setActive((prev) => (prev + 1) % items);
      }, 12000);
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [active, items, isPlaying]);

  useEffect(() => {
    setClassName("");

    if (!isPlaying) {
      return;
    }

    requestAnimationFrame(() => {
      setClassName(styles.inPlay);
    });
  }, [active, isPlaying]);

  return { stop, active, setActive, className };
}

export const ContentfulTextAndMediaCarousel: ContentfulComponentType<
  TextAndMediaCarousel
> = ({ textAndMediaItemsCollection }) => {
  const items = textAndMediaItemsCollection?.items.filter(isDefined) ?? [];

  const { active, setActive, stop, className } = useSlideShow({
    items: items.length,
  });

  if (!items.length) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      {items.map((slide, index) => {
        const theme = getRedesignThemeColors(slide.theme);
        if (!theme) return null;
        const isActive = active === index;
        return (
          <Fragment key={`bg-${slide.sys?.id ?? index}`}>
            <div
              className={styles.gradientTop}
              style={{
                background: `linear-gradient(180deg, transparent 0%, ${theme.backgroundColor} 100%)`,
                opacity: isActive ? 1 : 0,
              }}
            />
            <div
              className={styles.background}
              style={{
                backgroundColor: theme.backgroundColor,
                opacity: isActive ? 1 : 0,
              }}
            />
            <div
              className={styles.gradientBottom}
              style={{
                background: `linear-gradient(0deg, transparent 0%, ${theme.backgroundColor} 100%)`,
                opacity: isActive ? 1 : 0,
              }}
            />
          </Fragment>
        );
      })}
      <div className={styles.content}>
        <div className={styles.layout}>
          <div className={styles.textColumn}>
            <div className={styles.controls}>
              {items.map((item, i) => (
                <button
                  key={i}
                  onClick={() => {
                    stop();
                    setActive(i);
                  }}
                  className={classNames(
                    styles.control,
                    active === i && styles.active
                  )}
                >
                  {active === i && (
                    <div
                      className={classNames(
                        styles.progress,
                        active === i && className
                      )}
                    />
                  )}
                  <span className={styles.controlLabel}>
                    {item.highlight}
                  </span>
                </button>
              ))}
            </div>
            <div className={styles.slideTextArea}>
              {items.map((slide, index) => (
                <div
                  key={slide.sys?.id ?? index}
                  className={classNames(
                    styles.slideContent,
                    active === index && styles.slideActive
                  )}
                >
                  {slide.title && (
                    <h2 className={styles.title}>{slide.title}</h2>
                  )}
                  {slide.text && (
                    <p className={styles.body}>
                      <Markdown
                        allowedElements={["strong", "em", "a"]}
                        unwrapDisallowed
                        urlTransform={markdownUrlTransform}
                      >
                        {slide.text}
                      </Markdown>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.imageColumn}>
            <div className={styles.imageWrapper}>
              {items.map((slide, index) => (
                <div
                  key={slide.sys?.id ?? index}
                  className={classNames(
                    styles.slideImage,
                    active === index && styles.slideActive
                  )}
                >
                  <ContentfulMedia
                    media={slide.media ?? null}
                    sizes="650px"
                    display="square"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ContentfulTextAndMediaCarousel.fragment = gql`
  fragment TextAndMediaCarousel on ComponentTextAndMediaCarousel {
    sys {
      id
      publishedAt
    }
    textAndMediaItemsCollection(limit: 4) {
      items {
        ...TextAndMedia
      }
    }
  }
`;
